import React, { useEffect, useState } from 'react'
import SelectPersonModal from './SelectPersonModal'
import tenantApi from '../api/tenant/TenantApi'
import { PersonLiteDto } from '../api/person/models/PersonDto'
import personApi from '../api/person/PersonApi'
import roleApi from '../api/role/RoleApi'
import { toast } from 'sonner'
import Button from './Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStar } from '@fortawesome/free-solid-svg-icons'

type Props = {
    roleId: string
    currentNetworkSeedIds: string[]
    onClose: (hmsChanged: boolean) => void
    showNetworkLinksButton?: boolean
    showHiringTeamButton?: boolean
    onEphemeralNetworkSeedsAdded?: (personIds: string[]) => void
}

function AddHiringTeamModal({
    roleId,
    currentNetworkSeedIds,
    onClose,
    showNetworkLinksButton = true,
    showHiringTeamButton = true,
    onEphemeralNetworkSeedsAdded,
}: Props): React.JSX.Element {
    const [availableHMPersons, setAvailableHMPersons] = useState<
        PersonLiteDto[]
    >([])
    const [isModalLoading, setIsModalLoading] = useState<boolean>(false)
    const [enableButtons, setEnableButtons] = useState<boolean>(false)
    const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([])

    useEffect(() => {
        async function fetchAvailableHMs() {
            setIsModalLoading(true)
            // TODO: in future this needs to be paginated
            const availableSeedIds = (await tenantApi.getAvailableSeeds())
                .availableSeedIds

            // remove the current hiring managers from the available list
            const unselectedAvailableSeedIds = availableSeedIds.filter(
                (id) =>
                    !currentNetworkSeedIds.includes(id) &&
                    !selectedPersonIds.includes(id), // optional extra check
            )

            // now get the PersonLite from the server, using the cache if the persons are in there
            const availableHMPersons = (
                await personApi.getPersonsLite({
                    personIds: unselectedAvailableSeedIds,
                })
            ).persons

            setAvailableHMPersons(availableHMPersons)
            setIsModalLoading(false)
        }
        fetchAvailableHMs()
    }, [roleId, currentNetworkSeedIds])

    async function handleSelectPersonChanged(
        person: PersonLiteDto,
        selected: boolean,
    ) {
        const personId = person.id
        let newSelectedPersonIds: string[]

        if (selected) {
            newSelectedPersonIds = [...selectedPersonIds, personId]
            setSelectedPersonIds(newSelectedPersonIds)
        } else {
            newSelectedPersonIds = selectedPersonIds.filter(
                (id) => id !== personId,
            )
            setSelectedPersonIds(newSelectedPersonIds)
        }

        if (newSelectedPersonIds.length > 0) {
            setEnableButtons(true)
        } else {
            setEnableButtons(false)
        }

        // setIsModalLoading(true)

        // try {
        //     console.log('select new hiring manager!')
        //     // await roleApi.addHiringTeamMember({
        //     //     hiringManagerId: person.id,
        //     //     roleId: roleId,
        //     // })

        //     // setIsModalLoading(false)
        //     // onClose(true)
        // } catch (error) {
        //     console.error(error)
        //     toast.error(
        //         `There was a problem adding the hiring manager, please try again.`,
        //         {
        //             duration: 5000,
        //         },
        //     )
        // } finally {
        //     setIsModalLoading(false)
        // }
    }

    async function addToHiringTeam() {
        setIsModalLoading(true)

        try {
            await roleApi.addHiringTeamMembers({
                hiringManagerIds: selectedPersonIds,
                roleId: roleId,
            })

            onClose(true)
        } catch (error) {
            console.error(error)
            toast.error(
                `There was a problem adding the hiring managers, please try again.`,
                {
                    duration: 5000,
                },
            )
        } finally {
            setIsModalLoading(false)
        }
    }

    async function addToNetworkLinks() {
        if (onEphemeralNetworkSeedsAdded) {
            onEphemeralNetworkSeedsAdded(selectedPersonIds)
            onClose(false)
            return
        }

        // fallback to normal API call if not ephemeral
        setIsModalLoading(true)

        try {
            await roleApi.addNetworkLinks({
                personIds: selectedPersonIds,
                roleId: roleId,
            })

            onClose(true)
        } catch (error) {
            console.error(error)
            toast.error(
                `There was a problem adding the hiring managers, please try again.`,
                {
                    duration: 5000,
                },
            )
        } finally {
            setIsModalLoading(false)
        }
    }

    return (
        <SelectPersonModal
            persons={availableHMPersons}
            title="Add Network Links"
            onClose={() => onClose(false)}
            onSelectChanged={handleSelectPersonChanged}
            multiSelectButtons={
                <div className="flex gap-2">
                    {showNetworkLinksButton && (
                        <Button
                            style="secondary"
                            type="button"
                            onClick={addToNetworkLinks}
                            enabled={enableButtons}
                        >
                            Add {selectedPersonIds.length} to Network Links
                        </Button>
                    )}
                    {showHiringTeamButton && (
                        <Button
                            style="primary"
                            type="button"
                            onClick={addToHiringTeam}
                            enabled={enableButtons}
                        >
                            <FontAwesomeIcon
                                icon={faStar}
                                className="text-white mr-2"
                                title="Hiring Manager"
                            />
                            Add {selectedPersonIds.length} to Hiring Team
                        </Button>
                    )}
                </div>
            }
            isLoading={isModalLoading}
        />
    )
}

export default AddHiringTeamModal
